import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// ============================================
// SVG Icons — 线条风格，跟底部导航栏统一
// ============================================
const Icons = {
  play: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6,3 20,12 6,21"/></svg>,
  pause: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="5" y="3" width="4" height="18" rx="1"/><rect x="15" y="3" width="4" height="18" rx="1"/></svg>,
  prev: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="3" y="4" width="3" height="16" rx="1"/><polygon points="21,4 10,12 21,20"/></svg>,
  next: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="18" y="4" width="3" height="16" rx="1"/><polygon points="3,4 14,12 3,20"/></svg>,
  sequence: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17,1 21,5 17,9"/><path d="M3,11V9a4,4,0,0,1,4-4h14"/><polyline points="7,23 3,19 7,15"/><path d="M21,13v2a4,4,0,0,1-4,4H3"/></svg>,
  single: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17,1 21,5 17,9"/><path d="M3,11V9a4,4,0,0,1,4-4h14"/><polyline points="7,23 3,19 7,15"/><path d="M21,13v2a4,4,0,0,1-4,4H3"/><text x="12" y="16" textAnchor="middle" fill="currentColor" stroke="none" fontSize="10" fontWeight="700">1</text></svg>,
  shuffle: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16,3 21,3 21,8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21,16 21,21 16,21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>,
  music: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  loading: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>,
};

// ============================================
// Music Context — 全局播放状态
// ============================================
const MusicContext = createContext();

export function useMusicPlayer() {
  return useContext(MusicContext);
}

export function MusicProvider({ children, apiBase = '' }) {
  const audioRef = useRef(null);
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lyrics, setLyrics] = useState([]);

  // 播放列表 + 模式
  const [playlist, setPlaylist] = useState([]);
  const playlistRef = useRef([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const currentIndexRef = useRef(-1);
  const [playMode, setPlayMode] = useState('sequence');

  useEffect(() => { playlistRef.current = playlist; }, [playlist]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  // 初始化Audio
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'auto';
    }
    const audio = audioRef.current;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => setDuration(audio.duration || 0);
    const onError = (e) => {
      console.error('[music] Audio error:', e);
      setError('播放出错了');
      setIsPlaying(false);
      setLoading(false);
    };
    const onCanPlay = () => setLoading(false);
    const onWaiting = () => setLoading(true);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('error', onError);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('waiting', onWaiting);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('waiting', onWaiting);
    };
  }, []);

  // 内部播放（自动切歌用）
  const _playByIndex = useCallback(async (idx) => {
    const pl = playlistRef.current;
    if (idx < 0 || idx >= pl.length) return;
    const song = pl[idx];
    setLoading(true);
    setError(null);
    setCurrentIndex(idx);
    currentIndexRef.current = idx;

    try {
      const res = await fetch(`${apiBase}/api/music/play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(song),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); return; }

      const streamUrl = data.stream_url.startsWith('http') ? data.stream_url : `${apiBase}${data.stream_url}`;
      const songData = {
        song_name: data.song_name, singer: data.singer, mid: song.mid,
        album_mid: song.album_mid || '', stream_url: streamUrl, quality: data.quality,
      };
      setCurrentSong(songData);

      try {
        const lyrRes = await fetch(`${apiBase}/api/music/lyrics/${song.mid}`);
        const lyrData = await lyrRes.json();
        setLyrics(lyrData.lyric ? parseLRC(lyrData.lyric) : []);
      } catch(e) { setLyrics([]); }

      const audio = audioRef.current;
      audio.src = songData.stream_url;
      try { await audio.play(); setIsPlaying(true); }
      catch(e) { console.warn('[music] Auto play failed:', e); setIsPlaying(false); }
      setLoading(false);
    } catch(err) {
      console.error('[music] Play error:', err);
      setError('网络错误');
      setLoading(false);
    }
  }, [apiBase]);

  // onEnded
  const playModeRef = useRef(playMode);
  useEffect(() => { playModeRef.current = playMode; }, [playMode]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onEnded = () => {
      const mode = playModeRef.current;
      const pl = playlistRef.current;
      const idx = currentIndexRef.current;

      if (mode === 'single') {
        audio.currentTime = 0;
        audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      } else if (mode === 'shuffle' && pl.length > 1) {
        let next = idx;
        while (next === idx) { next = Math.floor(Math.random() * pl.length); }
        _playByIndex(next);
      } else if (mode === 'sequence' && pl.length > 0) {
        const next = idx + 1;
        if (next < pl.length) { _playByIndex(next); }
        else { setIsPlaying(false); setCurrentTime(0); }
      } else { setIsPlaying(false); setCurrentTime(0); }
    };

    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, [_playByIndex]);

  // playSong（用户点击，含iOS解锁）
  const playSong = useCallback(async (song) => {
    setLoading(true);
    setError(null);

    const audio = audioRef.current;
    audio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
    audio.play().catch(() => {});

    const pl = playlistRef.current;
    const existIdx = pl.findIndex(s => s.mid === song.mid);
    if (existIdx >= 0) {
      setCurrentIndex(existIdx);
      currentIndexRef.current = existIdx;
    } else {
      const newPl = [...pl, song];
      setPlaylist(newPl);
      playlistRef.current = newPl;
      const newIdx = newPl.length - 1;
      setCurrentIndex(newIdx);
      currentIndexRef.current = newIdx;
    }

    try {
      const res = await fetch(`${apiBase}/api/music/play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(song),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); return; }

      const streamUrl = data.stream_url.startsWith('http') ? data.stream_url : `${apiBase}${data.stream_url}`;
      const songData = {
        song_name: data.song_name, singer: data.singer, mid: song.mid,
        album_mid: song.album_mid || '', stream_url: streamUrl, quality: data.quality,
      };
      setCurrentSong(songData);

      try {
        const lyrRes = await fetch(`${apiBase}/api/music/lyrics/${song.mid}`);
        const lyrData = await lyrRes.json();
        setLyrics(lyrData.lyric ? parseLRC(lyrData.lyric) : []);
      } catch(e) { setLyrics([]); }

      audio.src = songData.stream_url;
      try { await audio.play(); setIsPlaying(true); }
      catch(e) { console.warn('[music] Play failed:', e); setIsPlaying(false); }
      setLoading(false);
    } catch(err) {
      console.error('[music] Play error:', err);
      setError('网络错误');
      setLoading(false);
    }
  }, [apiBase]);

  const playNext = useCallback(() => {
    const pl = playlistRef.current;
    const idx = currentIndexRef.current;
    if (pl.length === 0) return;
    if (playModeRef.current === 'shuffle' && pl.length > 1) {
      let next = idx;
      while (next === idx) { next = Math.floor(Math.random() * pl.length); }
      _playByIndex(next);
    } else {
      _playByIndex((idx + 1) % pl.length);
    }
  }, [_playByIndex]);

  const playPrev = useCallback(() => {
    const pl = playlistRef.current;
    const idx = currentIndexRef.current;
    if (pl.length === 0) return;
    if (audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    _playByIndex((idx - 1 + pl.length) % pl.length);
  }, [_playByIndex]);

  const togglePlayMode = useCallback(() => {
    setPlayMode(m => m === 'sequence' ? 'single' : m === 'single' ? 'shuffle' : 'sequence');
  }, []);

  const setPlaylistAndPlay = useCallback((songs, startIndex = 0) => {
    setPlaylist(songs);
    playlistRef.current = songs;
    if (songs.length > 0) _playByIndex(startIndex);
  }, [_playByIndex]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !currentSong) return;
    if (isPlaying) { audio.pause(); setIsPlaying(false); }
    else { audio.play().then(() => setIsPlaying(true)).catch(() => {}); }
  }, [isPlaying, currentSong]);

  const seekTo = useCallback((time) => {
    if (audioRef.current) { audioRef.current.currentTime = time; setCurrentTime(time); }
  }, []);

  // 歌词引用（发到聊天）
  const [quotedLyric, setQuotedLyric] = useState(null);
  // AI听歌（发歌词给林晏）
  const [listenRequest, setListenRequest] = useState(null);
  // { song_name, singer, lyricsText }

  // 停止播放并清空（MiniPlayer的×用）
  const stopSong = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      try { audio.load(); } catch(e) {}
    }
    setIsPlaying(false);
    setCurrentSong(null);
    setLyrics([]);
    setCurrentTime(0);
    setDuration(0);
    setLoading(false);
    setError(null);
  }, []);

  const value = {
    currentSong, isPlaying, duration, currentTime, loading, error, lyrics,
    playlist, currentIndex, playMode,
    playSong, togglePlay, seekTo, stopSong,
    playNext, playPrev, togglePlayMode, setPlaylistAndPlay,
    quotedLyric, setQuotedLyric,
    listenRequest, setListenRequest,
  };

  return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
}

// LRC歌词解析
function parseLRC(lrcString) {
  if (!lrcString) return [];
  const lines = lrcString.split('\n');
  const result = [];
  for (const line of lines) {
    const match = line.match(/\[(\d{2}):(\d{2})\.(\d{2,3})\](.*)/);
    if (match) {
      const mins = parseInt(match[1]);
      const secs = parseInt(match[2]);
      const ms = parseInt(match[3]);
      const time = mins * 60 + secs + ms / (match[3].length === 3 ? 1000 : 100);
      const text = match[4].trim();
      if (text) result.push({ time, text });
    }
  }
  return result.sort((a, b) => a.time - b.time);
}


// 封面URL —— 网易云直接给完整URL，QQ音乐用album_mid拼接，兼容两种
function getCoverUrl(albumMid) {
  if (!albumMid) return null;
  if (albumMid.startsWith('http')) return albumMid;
  return `https://y.qq.com/music/photo_new/T002R300x300M000${albumMid}.jpg`;
}


// ============================================
// MiniPlayer
// ============================================
export function MiniPlayer() {
  const { currentSong, isPlaying, togglePlay, playNext, currentTime, duration, loading, stopSong } = useMusicPlayer();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (currentSong && location.pathname !== '/chat') {
      document.body.classList.add('has-mini-player');
    } else {
      document.body.classList.remove('has-mini-player');
    }
    return () => document.body.classList.remove('has-mini-player');
  }, [currentSong, location.pathname]);

  if (!currentSong) return null;
  if (location.pathname === '/chat') return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const coverUrl = currentSong.album_mid
    ? getCoverUrl(currentSong.album_mid)
    : null;

  return (
    <div className="mini-player" onClick={() => navigate('/music')}>
      <div className="mini-player-progress">
        <div className="mini-player-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="mini-player-content">
        <div className="mini-player-info">
          {coverUrl ? (
            <img className="mini-player-cover" src={coverUrl} alt="" />
          ) : (
            <div className="mini-player-icon">{Icons.music}</div>
          )}
          <div className="mini-player-text">
            <span className="mini-player-name">{currentSong.song_name}</span>
            <span className="mini-player-singer">{currentSong.singer}</span>
          </div>
        </div>
        <div className="mini-player-btns">
          <button
            className="mini-player-btn"
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            disabled={loading}
          >
            {loading ? Icons.loading : isPlaying ? Icons.pause : Icons.play}
          </button>
          <button
            className="mini-player-btn mini-next"
            onClick={(e) => { e.stopPropagation(); playNext(); }}
          >
            {Icons.next}
          </button>
          <button
            className="mini-player-close"
            onClick={(e) => { e.stopPropagation(); stopSong(); }}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================
// MusicPage
// ============================================
export function MusicPage() {
  const { currentSong } = useMusicPlayer();
  const [view, setView] = useState(currentSong ? 'player' : 'search');
  const navigate = useNavigate();
  const [musicBg, setMusicBg] = useState(localStorage.getItem('music_bg_url') || '');

  useEffect(() => {
    fetch('/api/profile-settings')
      .then(r => r.json())
      .then(data => {
        const i = data.find(d => d.key === 'music_bg_url');
        if (i && i.value) { setMusicBg(i.value); localStorage.setItem('music_bg_url', i.value); }
        else if (i && !i.value) { setMusicBg(''); localStorage.removeItem('music_bg_url'); }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="music-page">
      {view === 'player' && currentSong && currentSong.album_mid && (
        <>
          <div className="music-page-bg cover-mode" style={{ backgroundImage: `url(${getCoverUrl(currentSong.album_mid)})` }} />
          <div className="music-page-veil" />
        </>
      )}
      {musicBg && view !== 'player' && (
        <div className="music-page-bg" style={{ backgroundImage: `url(${musicBg})` }} />
      )}
      <div className="music-page-header">
        <button className="music-back-btn" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div className="music-tabs">
          <button className={`music-tab ${view === 'player' ? 'active' : ''}`} onClick={() => setView('player')}>在听</button>
          <button className={`music-tab ${view === 'search' ? 'active' : ''}`} onClick={() => setView('search')}>搜歌</button>
          <button className={`music-tab ${view === 'history' ? 'active' : ''}`} onClick={() => setView('history')}>历史</button>
          <button className={`music-tab ${view === 'stats' ? 'active' : ''}`} onClick={() => setView('stats')}>档案</button>
          <button className={`music-tab ${view === 'playlists' ? 'active' : ''}`} onClick={() => setView('playlists')}>歌单</button>
        </div>
        <div style={{ width: 32 }} />
      </div>
      {view === 'player' && <PlayerView />}
      {view === 'search' && <SearchView />}
      {view === 'history' && <HistoryView />}
      {view === 'stats' && <StatsView />}
      {view === 'playlists' && <PlaylistsView />}
    </div>
  );
}


// ============================================
// PlayerView
// ============================================
function PlayerView() {
  const {
    currentSong, isPlaying, togglePlay, currentTime, duration, seekTo, loading, lyrics,
    playNext, playPrev, playMode, togglePlayMode, playlist, setQuotedLyric, setListenRequest
  } = useMusicPlayer();
  const lyricsRef = useRef(null);
  const navigate = useNavigate();
  const longPressRef = useRef(null);
  const [lyricPopup, setLyricPopup] = useState(null);
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(false);
  const [showWriteMemory, setShowWriteMemory] = useState(false);
  const [showViewMemory, setShowViewMemory] = useState(false);
  // 黑胶/歌词视图切换
  const [showLyricsView, setShowLyricsView] = useState(false);
  // 头像（共戴耳机用）
  const [avatars, setAvatars] = useState({ me: null, him: null });

  useEffect(() => {
    fetch('/api/profile-settings')
      .then(r => r.json())
      .then(data => {
        const g = k => { const i = data.find(d => d.key === k); return i ? i.value : null; };
        setAvatars({ me: g('avatar_me'), him: g('avatar_him') });
      })
      .catch(() => {});
  }, []);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const modeIcon = playMode === 'sequence' ? Icons.sequence : playMode === 'single' ? Icons.single : Icons.shuffle;
  const modeLabel = playMode === 'sequence' ? '顺序' : playMode === 'single' ? '单曲' : '随机';

  const currentLyricIdx = lyrics.length > 0 ? (() => {
    let idx = 0;
    for (let i = 0; i < lyrics.length; i++) {
      if (lyrics[i].time <= currentTime) idx = i;
      else break;
    }
    return idx;
  })() : -1;

  useEffect(() => {
    if (lyricsRef.current && currentLyricIdx >= 0) {
      const el = lyricsRef.current.children[currentLyricIdx];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentLyricIdx]);

  if (!currentSong) {
    return (
      <div className="player-empty">
        <div className="player-empty-icon">{Icons.music}</div>
        <p>还没有放歌</p>
        <p className="player-empty-hint">去搜歌页找一首，或者让哥哥给你放</p>
      </div>
    );
  }

  return (
    <div className="player-view">

      {(!showLyricsView || lyrics.length === 0) ? (
        <div className="vinyl-scroll">
          {/* 共戴耳机的两个人 */}
          <div className="vinyl-couple">
            <svg className="vinyl-earphone-wire" viewBox="0 0 220 100" fill="none">
              {/* 左耳线：从左头像外缘绕下汇合 */}
              <path d="M52 42 C46 66, 92 74, 108 88" stroke="var(--text3)" strokeWidth="1.3" strokeLinecap="round" opacity="0.55"/>
              <path d="M168 42 C174 66, 128 74, 112 88" stroke="var(--text3)" strokeWidth="1.3" strokeLinecap="round" opacity="0.55"/>
              <path d="M110 88 C110 92, 110 96, 110 100" stroke="var(--text3)" strokeWidth="1.3" strokeLinecap="round" opacity="0.55"/>
              {/* 左耳塞：白色，贴左头像左缘 */}
              <g filter="url(#earbudShadow)">
                <line x1="52.5" y1="36" x2="52" y2="42" stroke="#fff" strokeWidth="2.6" strokeLinecap="round"/>
                <circle cx="53" cy="32" r="5" fill="#ffffff" stroke="rgba(0,0,0,0.06)" strokeWidth="0.5"/>
                <circle cx="51.4" cy="30.4" r="1.4" fill="rgba(0,0,0,0.06)"/>
              </g>
              {/* 右耳塞：贴右头像右缘 */}
              <g filter="url(#earbudShadow)">
                <line x1="167.5" y1="36" x2="168" y2="42" stroke="#fff" strokeWidth="2.6" strokeLinecap="round"/>
                <circle cx="167" cy="32" r="5" fill="#ffffff" stroke="rgba(0,0,0,0.06)" strokeWidth="0.5"/>
                <circle cx="165.4" cy="30.4" r="1.4" fill="rgba(0,0,0,0.06)"/>
              </g>
              <defs>
                <filter id="earbudShadow" x="-30%" y="-30%" width="160%" height="160%">
                  <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="rgba(0,0,0,0.22)"/>
                </filter>
              </defs>
            </svg>
            <div className="vinyl-avatar-row">
              <div className="vinyl-avatar">
                {avatars.him ? <img src={avatars.him} alt="" /> : <span>晏</span>}
              </div>
              <div className="vinyl-avatar">
                {avatars.me ? <img src={avatars.me} alt="" /> : <span>枝</span>}
              </div>
            </div>
          </div>

          {/* 黑胶唱盘 + 唱臂 */}
          <div className="vinyl-stage" onClick={() => { if (lyrics.length > 0) setShowLyricsView(true); }}>
            <div className={`vinyl-disc ${isPlaying ? 'spinning' : ''}`}>
              <div className="vinyl-grooves" />
              <div className="vinyl-sheen" />
              {currentSong.album_mid ? (
                <img className="vinyl-label" src={getCoverUrl(currentSong.album_mid)} alt="" />
              ) : (
                <div className="vinyl-label vinyl-label-empty">{Icons.music}</div>
              )}
              <div className="vinyl-spindle" />
            </div>
            {/* 唱臂 */}
            <svg className={`vinyl-tonearm ${isPlaying ? 'playing' : ''}`} viewBox="0 0 90 150" fill="none">
              <circle cx="66" cy="18" r="13" fill="rgba(255,255,255,0.92)" filter="drop-shadow(0 2px 4px rgba(0,0,0,0.18))"/>
              <circle cx="66" cy="18" r="5" fill="#d8d4cf"/>
              <path d="M66 18 C60 52, 66 74, 46 104" stroke="rgba(255,255,255,0.92)" strokeWidth="5" strokeLinecap="round" filter="drop-shadow(0 2px 3px rgba(0,0,0,0.15))"/>
              <rect x="36" y="102" width="18" height="10" rx="3" transform="rotate(-32 45 107)" fill="rgba(255,255,255,0.95)" filter="drop-shadow(0 2px 3px rgba(0,0,0,0.2))"/>
            </svg>
          </div>

          {/* 紧凑信息行 */}
          <div className="vinyl-info-row">
            <div className="vinyl-info-text">
              <h2 className="player-song-name">{currentSong.song_name}</h2>
              <p className="player-song-singer">{currentSong.singer} · <span className="vinyl-together-inline">和林晏一起在听</span></p>
            </div>
            <FavoriteBtn song={currentSong} />
          </div>
        </div>
      ) : (
        <>
          <div className="player-song-info lyrics-mode">
            <h2 className="player-song-name">{currentSong.song_name}</h2>
            <p className="player-song-singer">{currentSong.singer}</p>
            <button className="vinyl-back-btn" onClick={() => setShowLyricsView(false)}>
              <div className={`vinyl-mini ${isPlaying ? 'spinning' : ''}`}>
                {currentSong.album_mid && <img src={getCoverUrl(currentSong.album_mid)} alt="" />}
              </div>
            </button>
          </div>
          <div className="player-lyrics-wrap">
            <div className="player-lyrics" ref={lyricsRef}>
              {lyrics.map((line, i) => (
                <div
                  key={i}
                  className={'player-lyric-line' + (i === currentLyricIdx ? ' active' : '')}
                  onClick={() => seekTo(line.time)}
                  onTouchStart={() => {
                    longPressRef.current = setTimeout(() => {
                      setLyricPopup({ text: line.text, idx: i });
                      longPressRef.current = null;
                    }, 500);
                  }}
                  onTouchEnd={() => { if (longPressRef.current) clearTimeout(longPressRef.current); }}
                  onTouchMove={() => { if (longPressRef.current) clearTimeout(longPressRef.current); }}
                  onContextMenu={(e) => { e.preventDefault(); setLyricPopup({ text: line.text, idx: i }); }}
                >
                  {line.text}
                </div>
              ))}
            </div>
            {/* 长按歌词弹出 */}
            {lyricPopup && (
              <div className="lyric-popup-overlay" onClick={() => setLyricPopup(null)}>
                <div className="lyric-popup" onClick={(e) => e.stopPropagation()}>
                  <div className="lyric-popup-text">"{lyricPopup.text}"</div>
                  <div className="lyric-popup-meta">{currentSong.song_name} — {currentSong.singer}</div>
                  <div className="lyric-popup-actions">
                    <button className="lyric-popup-btn" onClick={() => {
                      setQuotedLyric({ text: lyricPopup.text, song_name: currentSong.song_name, singer: currentSong.singer });
                      setLyricPopup(null);
                      navigate('/chat');
                    }}>发到聊天</button>
                    <button className="lyric-popup-btn secondary" onClick={() => {
                      setQuotedLyric({ text: lyricPopup.text + '\n这句歌词什么意思？', song_name: currentSong.song_name, singer: currentSong.singer });
                      setLyricPopup(null);
                      navigate('/chat');
                    }}>问哥哥</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <div className="player-progress-wrap">
        <span className="player-time">{formatTime(currentTime)}</span>
        <div className="player-progress-bar">
          <input type="range" min={0} max={duration || 0} value={currentTime}
            onChange={(e) => seekTo(Number(e.target.value))} className="player-slider" />
        </div>
        <span className="player-time">{formatTime(duration)}</span>
      </div>

      {/* 操作小图标行：给哥哥听 / 加歌单 / 写记忆 / 看记忆 */}
      <div className="player-action-row">
        {lyrics.length > 0 && (
          <button className="player-action-btn" onClick={() => {
            const lyricsText = lyrics.map(l => l.text).join('\n');
            setListenRequest({
              song_name: currentSong.song_name,
              singer: currentSong.singer,
              album_mid: currentSong.album_mid || '',
              lyricsText,
            });
            navigate('/chat');
          }} title="给哥哥听">
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
            </svg>
          </button>
        )}
        <button className="player-action-btn" onClick={() => setShowAddToPlaylist(true)} title="加到歌单">
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="13" y2="6"/>
            <line x1="3" y1="12" x2="13" y2="12"/>
            <line x1="3" y1="18" x2="9" y2="18"/>
            <line x1="18" y1="12" x2="18" y2="18"/>
            <line x1="15" y1="15" x2="21" y2="15"/>
          </svg>
        </button>
        <button className="player-action-btn" onClick={() => setShowWriteMemory(true)} title="写记忆">
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
        </button>
        <button className="player-action-btn" onClick={() => setShowViewMemory(true)} title="看记忆">
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
          </svg>
        </button>
      </div>

      <div className="player-controls">
        <button className="player-ctrl-btn mode" onClick={togglePlayMode} title={modeLabel}>
          {modeIcon}
        </button>
        <button className="player-ctrl-btn" onClick={playPrev} disabled={playlist.length === 0}>
          {Icons.prev}
        </button>
        <button className="player-ctrl-btn main" onClick={togglePlay} disabled={loading}>
          {loading ? Icons.loading : isPlaying ? Icons.pause : Icons.play}
        </button>
        <button className="player-ctrl-btn" onClick={playNext} disabled={playlist.length === 0}>
          {Icons.next}
        </button>
        <button className="player-ctrl-btn mode playlist-count" disabled>
          {playlist.length > 0 ? playlist.length : '—'}
        </button>
      </div>

      {/* 写记忆弹窗 */}
      {showWriteMemory && currentSong && (
        <WriteMemoryModal song={currentSong} onClose={() => setShowWriteMemory(false)} />
      )}

      {/* 看记忆弹窗 */}
      {showViewMemory && currentSong && (
        <ViewMemoryModal song={currentSong} onClose={() => setShowViewMemory(false)} />
      )}

      {/* 加到歌单弹窗 */}
      {showAddToPlaylist && currentSong && (
        <AddToPlaylistModal song={currentSong} onClose={() => setShowAddToPlaylist(false)} />
      )}
    </div>
  );
}


// ============================================
// WriteMemoryModal — 写记忆弹窗（磨砂玻璃）
// ============================================
function WriteMemoryModal({ song, onClose }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await fetch('/api/music/memories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mid: song.mid, song_name: song.song_name, singer: song.singer,
          memory_text: text.trim(), created_by: 'zhizhi',
        }),
      });
      onClose();
    } catch(e) { console.error('Save memory error:', e); setSaving(false); }
  };

  return (
    <div className="memory-modal-overlay" onClick={onClose}>
      <div className="memory-modal" onClick={(e) => e.stopPropagation()}>
        <div className="memory-modal-title">写下这首歌的记忆</div>
        <div className="memory-modal-sub">{song.song_name} — {song.singer}</div>
        <textarea
          className="memory-modal-input"
          placeholder="这首歌让你想起什么..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          autoFocus
        />
        <button className="memory-modal-save" onClick={handleSave} disabled={saving || !text.trim()}>
          {saving ? '...' : '存进回忆里'}
        </button>
      </div>
    </div>
  );
}


// ============================================
// ViewMemoryModal — 看记忆弹窗（磨砂玻璃，可滚动）
// ============================================
function ViewMemoryModal({ song, onClose }) {
  const [memories, setMemories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!song?.mid) return;
    fetch(`/api/music/memories/${song.mid}`)
      .then(r => r.json())
      .then(d => { setMemories(d.memories || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [song?.mid]);

  return (
    <div className="memory-modal-overlay" onClick={onClose}>
      <div className="memory-modal" onClick={(e) => e.stopPropagation()}>
        <div className="memory-modal-title">
          {memories.length > 0 ? `${memories.length} 条记忆` : '这首歌的记忆'}
        </div>
        <div className="memory-modal-sub">{song.song_name} — {song.singer}</div>
        <div className="memory-modal-list">
          {!loaded ? (
            <div className="memory-modal-empty">加载中...</div>
          ) : memories.length === 0 ? (
            <div className="memory-modal-empty">还没有记忆，点 + 写下第一条</div>
          ) : (
            memories.map(m => (
              <div key={m.id} className="memory-modal-item">
                <div className="memory-modal-text">{m.memory_text}</div>
                <div className="memory-modal-meta">
                  {m.created_by === 'aiden' ? '林晏' : '知枝'} · {new Date(m.created_at).toLocaleDateString('zh-CN')}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================
// SearchView
// ============================================
function SearchView() {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const { setPlaylistAndPlay, loading: playLoading } = useMusicPlayer();

  const handleSearch = async () => {
    if (!keyword.trim()) return;
    setSearching(true);
    setSearchError(null);
    try {
      const res = await fetch('/api/music/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: keyword.trim(), num: 15 }),
      });
      const data = await res.json();
      if (data.error) { setSearchError(data.error); setResults([]); }
      else { setResults(data.songs || []); }
    } catch (err) { setSearchError('搜索失败，检查网络'); }
    setSearching(false);
  };

  const handlePlay = (song, idx) => {
    const pl = results.map(s => ({
      mid: s.mid, song_name: s.name, singer: s.singer, album_mid: s.album_mid || '',
    }));
    setPlaylistAndPlay(pl, idx);
  };

  return (
    <div className="search-view">
      <div className="search-input-wrap">
        <input type="text" className="search-input" placeholder="搜歌名、歌手..."
          value={keyword} onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
        <button className="search-btn" onClick={handleSearch}
          disabled={searching || !keyword.trim()}>
          {searching ? '...' : '搜索'}
        </button>
      </div>
      {searchError && <div className="search-error">{searchError}</div>}
      <div className="search-results">
        {results.map((song, i) => (
          <div key={`${song.mid}-${i}`} className="search-result-item">
            {song.album_mid && (
              <img className="search-result-cover" src={getCoverUrl(song.album_mid)} alt="" />
            )}
            <div className="search-result-info">
              <span className="search-result-name">{song.name}</span>
              <span className="search-result-singer">
                {song.singer}{song.album ? ` · ${song.album}` : ''}
              </span>
            </div>
            <button className="search-result-play" onClick={() => handlePlay(song, i)}
              disabled={playLoading}>
              {playLoading ? '...' : '播放'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================
// HistoryView
// ============================================
function HistoryView() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playSong } = useMusicPlayer();

  useEffect(() => {
    fetch('/api/music/history')
      .then(res => res.json())
      .then(data => { setHistory(data.history || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMins = Math.floor((now - d) / 60000);
    if (diffMins < 1) return '刚刚';
    if (diffMins < 60) return `${diffMins}分钟前`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}小时前`;
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  if (loading) return <div className="history-loading">加载中...</div>;
  if (history.length === 0) {
    return (
      <div className="history-empty">
        <p>还没有听过歌</p>
        <p className="history-empty-hint">第一首歌等你选</p>
      </div>
    );
  }

  return (
    <div className="history-view">
      <div className="history-count">一起听了 {history.length} 首歌</div>
      {history.map((item) => (
        <div key={item.id} className="history-item">
          <div className="history-item-info">
            <span className="history-item-name">{item.song_name}</span>
            <span className="history-item-singer">{item.singer}</span>
          </div>
          <div className="history-item-right">
            <span className="history-item-time">{formatDate(item.played_at)}</span>
            <button className="history-item-play"
              onClick={() => playSong({ mid: item.mid, song_name: item.song_name, singer: item.singer, album_mid: item.album_mid || '' })}>
              {Icons.play}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}


// ============================================
// FavoriteBtn — 红心按钮
// ============================================
function FavoriteBtn({ song }) {
  const [fav, setFav] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!song?.mid) return;
    setChecking(true);
    fetch(`/api/music/favorites/check/${song.mid}`)
      .then(r => r.json())
      .then(d => { setFav(d.favorited); setChecking(false); })
      .catch(() => setChecking(false));
  }, [song?.mid]);

  const toggle = async (e) => {
    e.stopPropagation();
    const res = await fetch('/api/music/favorites/toggle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mid: song.mid, song_name: song.song_name, singer: song.singer, album_mid: song.album_mid || '' }),
    });
    const d = await res.json();
    setFav(d.favorited);
  };

  if (checking) return null;
  return (
    <button className={`fav-btn ${fav ? 'active' : ''}`} onClick={toggle}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill={fav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    </button>
  );
}


// ============================================
// FavoritesView — 收藏列表（内部使用）
// ============================================
function FavoritesView() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playSong, setPlaylistAndPlay } = useMusicPlayer();

  useEffect(() => {
    fetch('/api/music/favorites')
      .then(r => r.json())
      .then(d => { setFavorites(d.favorites || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="history-loading">加载中...</div>;
  if (favorites.length === 0) return null;

  const playAll = () => {
    const pl = favorites.map(s => ({ mid: s.mid, song_name: s.song_name, singer: s.singer, album_mid: s.album_mid || '' }));
    setPlaylistAndPlay(pl, 0);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="fav-header">
        <div className="history-count">{favorites.length} 首收藏</div>
        <button className="fav-play-all" onClick={playAll}>播放全部</button>
      </div>
      {favorites.map((item) => (
        <div key={item.id} className="search-result-item"
          onClick={() => playSong({ mid: item.mid, song_name: item.song_name, singer: item.singer, album_mid: item.album_mid || '' })}>
          {item.album_mid && (
            <img className="search-result-cover" src={getCoverUrl(item.album_mid)} alt="" />
          )}
          <div className="search-result-info">
            <span className="search-result-name">{item.song_name}</span>
            <span className="search-result-singer">{item.singer}</span>
          </div>
        </div>
      ))}
    </div>
  );
}


// ============================================
// PlaylistsView — 歌单tab（含收藏+自定义歌单）
// ============================================
function PlaylistsView() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [openPlaylist, setOpenPlaylist] = useState(null);

  const fetchPlaylists = () => {
    fetch('/api/music/playlists')
      .then(r => r.json())
      .then(d => { setPlaylists(d.playlists || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchPlaylists(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch('/api/music/playlists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      const d = await res.json();
      if (d.success) {
        setNewName('');
        setShowCreate(false);
        fetchPlaylists();
      }
    } catch(e) {}
    setCreating(false);
  };

  const handleDelete = async (id) => {
    await fetch(`/api/music/playlists/${id}`, { method: 'DELETE' });
    fetchPlaylists();
  };

  if (openPlaylist) {
    return <PlaylistDetailView playlist={openPlaylist} onBack={() => { setOpenPlaylist(null); fetchPlaylists(); }} />;
  }

  return (
    <div className="history-view">
      {/* 红心收藏区 */}
      <FavoritesView />

      {/* 我的歌单 */}
      <div className="playlist-section-header">
        <span className="stats-section-title">我的歌单</span>
        <button className="song-memory-add" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? '取消' : '+ 新建'}
        </button>
      </div>

      {showCreate && (
        <div className="song-memory-input-wrap" style={{ marginBottom: 12 }}>
          <input className="song-memory-input" placeholder="歌单名称..." value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
          <button className="song-memory-save" onClick={handleCreate} disabled={creating || !newName.trim()}>
            {creating ? '...' : '创建'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="history-loading">加载中...</div>
      ) : playlists.length === 0 ? (
        <div className="playlist-empty-hint">还没有歌单，点右上角新建一个</div>
      ) : (
        playlists.map(pl => (
          <div key={pl.id} className="playlist-card" onClick={() => setOpenPlaylist(pl)}>
            <div className="playlist-card-cover">
              {pl.cover_album_mid ? (
                <img src={getCoverUrl(pl.cover_album_mid)} alt="" />
              ) : (
                <div className="playlist-card-icon">{Icons.music}</div>
              )}
            </div>
            <div className="playlist-card-info">
              <div className="playlist-card-name">{pl.name}</div>
              <div className="playlist-card-count">{pl.song_count || 0} 首</div>
            </div>
            <button className="playlist-card-del" onClick={(e) => { e.stopPropagation(); handleDelete(pl.id); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        ))
      )}
    </div>
  );
}


// ============================================
// PlaylistDetailView — 歌单详情
// ============================================
function PlaylistDetailView({ playlist, onBack }) {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playSong, setPlaylistAndPlay } = useMusicPlayer();

  useEffect(() => {
    fetch(`/api/music/playlists/${playlist.id}/songs`)
      .then(r => r.json())
      .then(d => { setSongs(d.songs || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [playlist.id]);

  const removeSong = async (mid) => {
    await fetch(`/api/music/playlists/${playlist.id}/songs/${mid}`, { method: 'DELETE' });
    setSongs(prev => prev.filter(s => s.mid !== mid));
  };

  const playAll = () => {
    const pl = songs.map(s => ({ mid: s.mid, song_name: s.song_name, singer: s.singer, album_mid: s.album_mid || '' }));
    setPlaylistAndPlay(pl, 0);
  };

  return (
    <div className="history-view">
      <div className="playlist-detail-header">
        <button className="playlist-back" onClick={onBack}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div className="playlist-detail-title">{playlist.name}</div>
        {songs.length > 0 && <button className="fav-play-all" onClick={playAll}>播放全部</button>}
      </div>

      {loading ? (
        <div className="history-loading">加载中...</div>
      ) : songs.length === 0 ? (
        <div className="playlist-empty-hint">歌单是空的，去播放器里把喜欢的歌加进来</div>
      ) : (
        songs.map(s => (
          <div key={s.id} className="search-result-item">
            {s.album_mid && (
              <img className="search-result-cover" src={getCoverUrl(s.album_mid)} alt="" />
            )}
            <div className="search-result-info"
              onClick={() => playSong({ mid: s.mid, song_name: s.song_name, singer: s.singer, album_mid: s.album_mid || '' })}>
              <span className="search-result-name">{s.song_name}</span>
              <span className="search-result-singer">{s.singer}</span>
            </div>
            <button className="playlist-card-del" onClick={() => removeSong(s.mid)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        ))
      )}
    </div>
  );
}


// ============================================
// AddToPlaylistModal — 加到歌单弹窗
// ============================================
function AddToPlaylistModal({ song, onClose }) {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch('/api/music/playlists')
      .then(r => r.json())
      .then(d => { setPlaylists(d.playlists || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const addToPlaylist = async (plId) => {
    setAdding(plId);
    try {
      await fetch(`/api/music/playlists/${plId}/songs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mid: song.mid, song_name: song.song_name, singer: song.singer, album_mid: song.album_mid || '' }),
      });
      setDone(plId);
      setTimeout(() => onClose(), 600);
    } catch(e) {}
    setAdding(null);
  };

  return (
    <div className="lyric-popup-overlay" onClick={onClose}>
      <div className="lyric-popup" onClick={(e) => e.stopPropagation()} style={{ width: '80%', maxWidth: 280 }}>
        <div className="lyric-popup-meta" style={{ marginBottom: 12, fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>加到歌单</div>
        {loading ? (
          <div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 13 }}>加载中...</div>
        ) : playlists.length === 0 ? (
          <div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 13 }}>还没有歌单，去歌单页新建一个</div>
        ) : (
          playlists.map(pl => (
            <div key={pl.id} className="add-playlist-item" onClick={() => addToPlaylist(pl.id)}>
              <span>{pl.name}</span>
              <span className="add-playlist-status">
                {done === pl.id ? '✓' : adding === pl.id ? '...' : `${pl.song_count || 0}首`}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


// ============================================
// StatsView — 播放档案
// ============================================
function StatsView() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { playSong } = useMusicPlayer();

  useEffect(() => {
    fetch('/api/music/stats')
      .then(r => r.json())
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="history-loading">加载中...</div>;
  if (!stats) return <div className="history-empty"><p>暂无数据</p></div>;

  const maxCount = stats.topSongs.length > 0 ? stats.topSongs[0].count : 1;

  return (
    <div className="stats-view">
      {/* 总览 */}
      <div className="stats-overview">
        <div className="stats-total">
          <div className="stats-total-num">{stats.total}</div>
          <div className="stats-total-label">首歌的旅程</div>
        </div>
        {stats.firstSong && (
          <div className="stats-first">
            第一首歌：{stats.firstSong.song_name} — {stats.firstSong.singer}
            <span className="stats-first-date"> · {new Date(stats.firstSong.played_at).toLocaleDateString('zh-CN')}</span>
          </div>
        )}
      </div>

      {/* 最近7天 */}
      {stats.recentDays.length > 0 && (
        <div className="stats-section">
          <div className="stats-section-title">最近7天</div>
          <div className="stats-bar-chart">
            {stats.recentDays.map(d => {
              const maxDay = Math.max(...stats.recentDays.map(x => x.count)) || 1;
              return (
                <div key={d.date} className="stats-bar-item">
                  <div className="stats-bar-fill" style={{ height: `${(d.count / maxDay) * 60}px` }} />
                  <div className="stats-bar-count">{d.count}</div>
                  <div className="stats-bar-date">{d.date.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 最常听 */}
      {stats.topSongs.length > 0 && (
        <div className="stats-section">
          <div className="stats-section-title">最常听</div>
          {stats.topSongs.map((song, i) => (
            <div key={song.mid} className="stats-song-item"
              onClick={() => playSong({ mid: song.mid, song_name: song.song_name, singer: song.singer, album_mid: song.album_mid })}>
              <div className="stats-song-rank">{i + 1}</div>
              {song.album_mid && (
                <img className="stats-song-cover" src={getCoverUrl(song.album_mid)} alt="" />
              )}
              <div className="stats-song-info">
                <div className="stats-song-name">{song.song_name}</div>
                <div className="stats-song-singer">{song.singer}</div>
              </div>
              <div className="stats-song-count">
                <div className="stats-song-bar" style={{ width: `${(song.count / maxCount) * 60}px` }} />
                <span>{song.count}次</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default { MusicProvider, MiniPlayer, MusicPage, useMusicPlayer };
